import { useEffect, useRef } from "react";
import { logVideoPlaybackError } from "../../videoDiagnostics";

// Shared by GalleryCard.tsx/MediaPreviewCard.tsx/MemoriesRow.tsx - extracted
// from three independent, byte-for-byte-identical copies of this same
// poster-frame-seek effect (video playback audit, Low #11: drift risk from
// a triplicated implementation - a future fix to one wouldn't have
// automatically applied to the other two). Also closes the audit's High #6
// finding: the three original copies all called video.load() + seek
// unconditionally on mount, so every video-type card in a grid started
// fetching and seeking concurrently regardless of whether it was actually
// on screen - real self-inflicted bandwidth/decode-pipeline contention on
// mobile, worst against iOS's more constrained concurrent hardware decode
// limit. Now gated behind IntersectionObserver: a card's video only starts
// loading once it's actually near the viewport.
const ROOT_MARGIN = "200px";

// onDuration is optional and best-effort - real data straight off the
// video element's own metadata once it loads (never guessed/fabricated),
// used by GalleryCard.tsx's duration badge. Callers that don't need it
// (MediaPreviewCard.tsx/MemoriesRow.tsx) just omit the argument.
export function useVideoPosterFrame(filename: string, isVideo: boolean, context: string, onDuration?: (seconds: number) => void) {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (!isVideo) return;
    const v = videoRef.current;
    if (!v) return;

    v.muted = true;
    v.playsInline = true;

    let started = false;
    const cleanup = () => {
      v.removeEventListener("loadedmetadata", onLoadedMeta);
      v.removeEventListener("seeked", onSeeked);
      v.removeEventListener("error", onError);
    };
    const onLoadedMeta = () => {
      if (Number.isFinite(v.duration)) onDuration?.(v.duration);
      try {
        v.currentTime = 0.05;
      } catch {
        // ignore
      }
    };
    const onSeeked = () => {
      try {
        v.pause();
      } catch {
        // ignore
      }
      cleanup();
    };
    const onError = () => {
      logVideoPlaybackError(context, v, filename);
      cleanup();
    };

    function start() {
      if (started) return;
      started = true;
      v!.preload = "metadata";
      v!.addEventListener("loadedmetadata", onLoadedMeta, { once: true });
      v!.addEventListener("seeked", onSeeked, { once: true });
      v!.addEventListener("error", onError, { once: true });
      try {
        v!.load();
      } catch {
        // ignore
      }
    }

    // IntersectionObserver isn't universally available in every environment
    // this app runs in (e.g. jsdom test runs) - fall back to starting
    // immediately rather than a poster frame that never loads at all.
    if (typeof IntersectionObserver === "undefined") {
      start();
      return cleanup;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          start();
          observer.disconnect();
        }
      },
      { rootMargin: ROOT_MARGIN },
    );
    observer.observe(v);

    return () => {
      observer.disconnect();
      cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVideo, filename, onDuration]);

  return videoRef;
}
